import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, CardHeader, Spinner } from "reactstrap";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TopTriggeredRules = ({ data }) => {
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!data) return;

    if (data.length === 0) {
      setCategories([]);
      setSeries([]);
      setLoading(false);
      return;
    }

    // Normalize data structure
    const normalized = data.map(item => ({
      rule: item.rule || item.name || "Unknown Rule",
      count: item.count || item.triggeredCount || 0
    }));

    // Sort and get top 10
    const sorted = normalized.sort((a, b) => b.count - a.count);
    const top10 = sorted.slice(0, 10);

    // Use actual counts (not percentages)
    const counts = top10.map(i => i.count);
    const labels = top10.map(i => i.rule.length > 35 ? i.rule.substring(0, 35) + '...' : i.rule);

    setCategories(labels);
    setSeries([{ name: "Triggers", data: counts }]);

    setLoading(false);
  }, [data]);

  const options = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "65%",
        borderRadius: 4,
        distributed: false
      }
    },
    xaxis: {
      categories: categories,
      title: {
        text: "Trigger Count",
        style: {
          color: ORANGE_COLORS.dark,
          fontWeight: 600
        }
      },
      labels: {
        formatter: (val) => val.toLocaleString()
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '11px',
          colors: ORANGE_COLORS.dark
        }
      }
    },
    colors: [ORANGE_COLORS.primary],
    dataLabels: {
      enabled: true,
      formatter: (val) => val.toLocaleString(),
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: ['#fff']
      }
    },
    legend: {
      show: false
    },
    tooltip: {
      y: {
        formatter: (val) => `${val.toLocaleString()} triggers`
      }
    },
    grid: {
      borderColor: '#f1f1f1',
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card style={{
        background: "var(--vz-secondary-bg, #fff)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden"
      }}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: "1px solid #FF5B00",
              background: "rgba(255, 107, 53, 0.24)",
              outline: "1px solid rgba(255, 107, 53, 0.25)",
              outlineOffset: "-2px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 10,
              flexShrink: 0,
              boxSizing: "border-box"
            }}>
              <i className="ri-bar-chart-horizontal-line" style={{ color: ORANGE_COLORS.primary, fontSize: 18, lineHeight: 1 }}></i>
            </span>
            Top Triggered Rules
          </h5>
          <small className="text-muted">Most frequently detected rules</small>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  // No data state
  if (series.length === 0 || categories.length === 0) {
    return (
      <Card style={{
        background: "var(--vz-secondary-bg, #fff)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden"
      }}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${ORANGE_COLORS.primary}`,
              background: "rgba(255, 107, 53, 0.08)",
              boxSizing: "border-box",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 8,
              flexShrink: 0
            }}>
              <i className="ri-bar-chart-horizontal-line" style={{ color: ORANGE_COLORS.primary, fontSize: 18, lineHeight: 1 }}></i>
            </span>
            Top Triggered Rules
          </h5>
        </CardHeader>
        <CardBody>
          <div className="text-center py-5">
            <span style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              border: `1px solid ${ORANGE_COLORS.primary}`,
              background: "rgba(255, 107, 53, 0.04)",
              boxSizing: "border-box",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
              flexShrink: 0
            }}>
              <i className="ri-bar-chart-horizontal-line" style={{ color: ORANGE_COLORS.primary, fontSize: 19, lineHeight: 1 }}></i>
            </span>
            <p className="text-muted">No rules trigger data available</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Chart display
  return (
    <Card style={{
      background: "var(--vz-secondary-bg, #fff)",
      border: "1px solid var(--vz-border-color)",
      borderRadius: 10,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      overflow: "hidden"
    }}>
      <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
        <h5 className="card-title mb-0">
          <span style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
              background: toRgba(ORANGE_COLORS.primary, 0.10),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 8,
              flexShrink: 0
            }}>
            <i
              className="ri-bar-chart-horizontal-line"
              style={{
                color: "#FF5B00",
                fontSize: 18,
                lineHeight: 1
              }}
            />
          </span>

          Top Triggered Rules
        </h5>
        <small className="text-muted">Most frequently detected rules</small>
      </CardHeader>
      <CardBody>
        <ReactApexChart
          dir="ltr"
          className="apex-charts"
          options={options}
          series={series}
          type="bar"
          height={350}
        />
      </CardBody>
    </Card>
  );
};

export default TopTriggeredRules;
